import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isMobileBreakpoint, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';

const PROMO_VIDEO_SELECTOR = '.mega-menu__promo-video';
const MAX_INTENT_PRELOAD_VIDEOS = 2;

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const summary = this.refs.details.querySelector('summary');

    this.addEventListener('keyup', this.#onKeyUp, { signal });
    summary?.addEventListener('pointerdown', this.#prepareIntentPromoVideos, { signal });
    summary?.addEventListener('touchstart', this.#prepareIntentPromoVideos, { signal, passive: true });
    summary?.addEventListener('focus', this.#prepareIntentPromoVideos, { signal });
    this.#setupDetailsVideoListeners(signal);
    this.#setupAnimatedElementListeners();
    this.#syncUtilitySocialLinks(signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  #prepareIntentPromoVideos = () => {
    preparePromoVideos(this.refs.details, {
      includeClosedDetails: true,
      maxVideos: MAX_INTENT_PRELOAD_VIDEOS,
      preload: 'auto',
    });
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      playPromoVideos(details);
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    pausePromoVideos(details);
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach((element) => {
          if (element instanceof HTMLDetailsElement) reset(element);
        });
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Plays promo videos when nested drawer details or accordions are opened.
   * @param {AbortSignal} signal
   */
  #setupDetailsVideoListeners(signal) {
    const nestedDetails = this.querySelectorAll('details:not([ref="details"])');

    nestedDetails.forEach((details) => {
      details.addEventListener(
        'toggle',
        () => {
          if (!(details instanceof HTMLDetailsElement)) return;

          if (details.open) {
            playPromoVideos(details);
          } else {
            pausePromoVideos(details);
          }
        },
        { signal }
      );
    });
  }

  /**
   * Syncs the mobile drawer social links from the Footer Utility social links block.
   * @param {AbortSignal} signal
   */
  #syncUtilitySocialLinks(signal) {
    const target = this.querySelector('[data-mobile-social-links]');

    if (!(target instanceof HTMLElement)) return;

    const render = () => {
      const source = document.querySelector('.footer-utilities__icons');

      if (!(source instanceof HTMLElement)) {
        target.hidden = true;
        return;
      }

      const sourceLinks = [...source.querySelectorAll('a[href]')].filter((link) => {
        if (!(link instanceof HTMLAnchorElement)) return false;
        if (link.getAttribute('aria-disabled') === 'true') return false;
        return link.getAttribute('href') !== '';
      });

      target.replaceChildren();

      if (!sourceLinks.length) {
        target.hidden = true;
        return;
      }

      const list = document.createElement('ul');
      list.className = 'menu-drawer__social-links-list';
      list.setAttribute('role', 'list');

      sourceLinks.forEach((sourceLink) => {
        const item = document.createElement('li');
        const link = sourceLink.cloneNode(true);

        if (link instanceof HTMLAnchorElement) {
          link.classList.add('menu-drawer__social-link');
        }

        item.append(link);
        list.append(item);
      });

      target.append(list);
      target.hidden = false;
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', render, { once: true, signal });
    } else {
      render();
    }

    document.addEventListener('shopify:section:load', render, { signal });
    document.addEventListener('shopify:block:select', render, { signal });

    const observer = new MutationObserver((mutations) => {
      if (mutations.every((mutation) => target.contains(mutation.target))) return;

      render();
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href', 'aria-disabled'],
    });
    signal.addEventListener('abort', () => observer.disconnect(), { once: true });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  pausePromoVideos(element);
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

/**
 * @param {Element | null | undefined} root
 */
function playPromoVideos(root) {
  preparePromoVideos(root, { preload: 'auto' });

  const videos = root?.querySelectorAll(PROMO_VIDEO_SELECTOR);
  videos?.forEach((video) => {
    if (video instanceof HTMLVideoElement) {
      if (!canAutoplayPromoVideo(video)) return;
      if (video.closest('details:not([open])')) return;

      video.play().catch(() => {});
    }
  });
}

/**
 * @param {Element | null | undefined} root
 */
function pausePromoVideos(root) {
  const videos = root?.querySelectorAll(PROMO_VIDEO_SELECTOR);
  videos?.forEach((video) => {
    if (video instanceof HTMLVideoElement) {
      video.pause();
    }
  });
}

/**
 * @param {Element | null | undefined} root
 * @param {{ includeClosedDetails?: boolean, maxVideos?: number, preload?: HTMLVideoElement['preload'] }} [options]
 */
function preparePromoVideos(root, options = {}) {
  if (!root || !canPreparePromoVideos()) return;

  const { includeClosedDetails = false, maxVideos = Infinity, preload = 'metadata' } = options;
  const videos = root.querySelectorAll(PROMO_VIDEO_SELECTOR);
  let preparedCount = 0;

  videos.forEach((video) => {
    if (!(video instanceof HTMLVideoElement)) return;
    if (preparedCount >= maxVideos) return;
    if (video.dataset.megaMenuVideoLoading !== 'intent') return;
    if (!includeClosedDetails && video.closest('details:not([open])')) return;

    video.preload = preload;

    if (video.dataset.megaMenuVideoPrepared !== preload) {
      video.load();
      video.dataset.megaMenuVideoPrepared = preload;
    }

    preparedCount++;
  });
}

/**
 * @returns {boolean}
 */
function canPreparePromoVideos() {
  if (!isMobileBreakpoint()) return false;
  if (prefersReducedMotion()) return false;

  const connection =
    navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  return !connection?.saveData;
}

/**
 * @param {HTMLVideoElement} video
 * @returns {boolean}
 */
function canAutoplayPromoVideo(video) {
  if (prefersReducedMotion()) return false;
  if (video.dataset.megaMenuVideoLoading !== 'intent') return true;

  return canPreparePromoVideos();
}
